/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Accordion, AccordionHeader, AccordionPanel, Button, Grid, Typography2, breakpoints } from '@coursera/cds-core';
import { TrashIcon } from '@coursera/cds-icons';

import FilterPills from 'bundles/enterprise-admin-user-filters/components/FilterPills';
import KeywordForm from 'bundles/enterprise-admin-user-filters/components/KeywordForm';
import { SubSection } from 'bundles/enterprise-admin-user-filters/components/ui';
import type { AdditionalFilterType, EnrichedData } from 'bundles/enterprise-admin-user-filters/types';
import { mapContractIdsToFilterOptions } from 'bundles/enterprise-admin-user-filters/utils';
import { ConfirmationDialog, Spacer, useModalState } from 'bundles/enterprise-ui';

import _t from 'i18n!nls/enterprise-admin-user-filters';

const AdditionalFilterSubSection = ({
  label,
  filter,
  values,
  enrichedData,
  onDismissAllValues,
  onDismissValue,
  onRemoveFilter,
  onSelect,
  onAdd,
}: {
  label: string;
  filter: AdditionalFilterType;
  values: string[];
  enrichedData: EnrichedData;
  onSelect?: () => void;
  onAdd?: (value: string) => void;
  onDismissValue: (id: string) => void;
  onDismissAllValues: () => void;
  onRemoveFilter: () => void;
}) => {
  const [removalConfirmationOpen, showRemovalConfirmation, hideRemovalConfirmation] = useModalState();
  const [keyword, setKeyword] = React.useState('');
  const [expanded, setExpanded] = React.useState(values.length === 0);
  return (
    <div>
      <Spacer size={16} />
      <SubSection>
        <Accordion variant="silent" expanded={expanded} onChange={(_, value) => setExpanded(value)}>
          <AccordionHeader
            label={_t('#{label}: #{clause}', {
              label,
              clause: values.length > 0 ? _t('selected #{label}', { label }) : _t('any'),
            })}
          />
          <AccordionPanel>
            {onAdd && <KeywordForm label={label} keyword={keyword} onChange={setKeyword} onSubmit={onAdd} />}
            <Spacer size={16} />

            {values.length > 0 ? (
              <Grid>
                <FilterPills
                  label={
                    onSelect ? _t('Selected #{label}', { label }) : _t('Keyword phrases to match #{label}', { label })
                  }
                  values={filter !== 'CONTRACT' ? values : undefined}
                  filterOptions={
                    filter === 'CONTRACT' ? mapContractIdsToFilterOptions(enrichedData, values) : undefined
                  }
                  numFiltersDisplayed={7}
                  onDismiss={onDismissValue}
                  onMore={onSelect}
                />
                <Spacer size={16} />
                {onSelect && (
                  <div css={{ display: 'flex', gap: 'var(--cds-spacing-200)' }}>
                    <Button
                      css={css`
                        ${breakpoints.down('xs')} {
                          width: 100%;
                          max-width: none;
                        }
                      `}
                      variant="secondary"
                      size="small"
                      onClick={onSelect}
                      data-testid="additional-filter-section-select_filled"
                    >
                      {_t('Select #{label}', { label })}
                    </Button>
                    <Button
                      css={css`
                        ${breakpoints.down('xs')} {
                          width: 100%;
                          max-width: none;
                        }
                      `}
                      variant="secondary"
                      size="small"
                      onClick={onDismissAllValues}
                      data-testid="additional-filter-section-clear-all"
                    >
                      {_t('Clear all #{label}', { label })}
                    </Button>
                  </div>
                )}
              </Grid>
            ) : (
              onSelect && (
                <Button
                  css={css`
                    ${breakpoints.down('xs')} {
                      width: 100%;
                      max-width: none;
                    }
                  `}
                  variant="secondary"
                  size="small"
                  onClick={onSelect}
                  data-testid="additional-filter-section-select_empty"
                >
                  {_t('Select #{label}', { label })}
                </Button>
              )
            )}
            <Spacer size={24} />
            <Button
              variant="ghost"
              size="small"
              icon={<TrashIcon size="small" />}
              onClick={showRemovalConfirmation}
              data-testid={`remove-filter-${filter}`}
            >
              {_t('Remove filter #{label}', { label })}
            </Button>
          </AccordionPanel>
        </Accordion>
      </SubSection>
      {removalConfirmationOpen && (
        <ConfirmationDialog
          open
          heading={_t('Do you want to remove the ‘#{filter}’ filter?', { filter: label })}
          confirmationLabel={_t('Remove filter')}
          onConfirm={onRemoveFilter}
          onClose={hideRemovalConfirmation}
        >
          <Typography2 component="p">
            {_t('This will remove the selected filter, you can always add this filters again.')}
          </Typography2>
        </ConfirmationDialog>
      )}
    </div>
  );
};
export default AdditionalFilterSubSection;
